import React, { createContext, forwardRef, useContext, useId } from 'react';
import { Icon, ICON_NAMES } from './Icon';
import { cx } from './cx';

// Field раздаёт полю внутри себя id, aria-describedby и признак ошибки,
// поэтому подпись, подсказка и ошибка связаны с полем без ручных id.
const FieldContext = createContext(null);

export function Field({ label, hint, error, required, optional, htmlFor, className, children }) {
    const autoId = useId();
    const id = htmlFor || `f${autoId.replace(/:/g, '')}`;
    const hintId = hint ? `${id}-hint` : undefined;
    const errorId = error ? `${id}-err` : undefined;
    const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

    return (
        <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error), required }}>
            <div className={cx('ds-field', className)}>
                {label && (
                    <label className="ds-field__label" htmlFor={id}>
                        {label}
                        {required && <span className="ds-field__req" aria-hidden="true">*</span>}
                        {optional && <span className="ds-field__opt">необязательно</span>}
                    </label>
                )}
                {children}
                {error ? (
                    <span className="ds-field__error" id={errorId} role="alert">
                        <Icon name="danger" size={14} />
                        {error}
                    </span>
                ) : hint ? (
                    <span className="ds-field__hint" id={hintId}>{hint}</span>
                ) : null}
            </div>
        </FieldContext.Provider>
    );
}

function useFieldProps({ id, invalid, required, ...rest }) {
    const ctx = useContext(FieldContext);
    const isInvalid = invalid ?? ctx?.invalid ?? false;
    return {
        isInvalid,
        props: {
            id: id ?? ctx?.id,
            required: required ?? ctx?.required,
            'aria-invalid': isInvalid || undefined,
            'aria-describedby': rest['aria-describedby'] ?? ctx?.describedBy,
        },
    };
}

function controlClass({ size, isInvalid, disabled, extra, className }) {
    return cx(
        'ds-control',
        size && size !== 'md' && `ds-control--${size}`,
        extra,
        isInvalid && 'is-invalid',
        disabled && 'is-disabled',
        className
    );
}

// prefix/suffix — иконка (строкой-именем) или короткий текст: «сом», «шт», «+996»
function Adorn({ value, side }) {
    if (value == null || value === false) return null;
    const content = typeof value === 'string' && ICON_NAMES.includes(value) ? <Icon name={value} size={16} /> : value;
    return <span className={`ds-control__adorn ds-control__adorn--${side}`}>{content}</span>;
}

export const Input = forwardRef(function Input(
    { size = 'md', prefix, suffix, invalid, disabled, className, style, onClear, ...rest },
    ref
) {
    const { isInvalid, props } = useFieldProps({ invalid, ...rest });
    const showClear = onClear && rest.value !== undefined && rest.value !== '';
    return (
        <div className={controlClass({ size, isInvalid, disabled, className })} style={style}>
            <Adorn value={prefix} side="start" />
            <input ref={ref} className="ds-control__input" disabled={disabled} {...rest} {...props} />
            {showClear && (
                <button type="button" className="ds-control__clear" onClick={onClear} aria-label="Очистить">
                    <Icon name="close" size={14} />
                </button>
            )}
            <Adorn value={suffix} side="end" />
        </div>
    );
});

export const SearchInput = forwardRef(function SearchInput({ placeholder = 'Поиск', ...rest }, ref) {
    return <Input ref={ref} type="search" prefix="search" placeholder={placeholder} {...rest} />;
});

export const Textarea = forwardRef(function Textarea(
    { invalid, disabled, className, style, rows = 4, ...rest },
    ref
) {
    const { isInvalid, props } = useFieldProps({ invalid, ...rest });
    return (
        <div className={controlClass({ isInvalid, disabled, extra: 'ds-control--textarea', className })} style={style}>
            <textarea ref={ref} className="ds-control__input" rows={rows} disabled={disabled} {...rest} {...props} />
        </div>
    );
});

// Нативный select — клавиатура, мобильные пикеры и доступность «бесплатно».
// options: [{ value, label, disabled }] или children <option>.
export const Select = forwardRef(function Select(
    { size = 'md', options, placeholder, invalid, disabled, className, style, children, ...rest },
    ref
) {
    const { isInvalid, props } = useFieldProps({ invalid, ...rest });
    return (
        <div className={controlClass({ size, isInvalid, disabled, extra: 'ds-control--select', className })} style={style}>
            <select ref={ref} className="ds-control__input" disabled={disabled} {...rest} {...props}>
                {placeholder && <option value="" disabled={rest.required}>{placeholder}</option>}
                {options
                    ? options.map((o) => (
                          <option key={o.value} value={o.value} disabled={o.disabled}>
                              {o.label}
                          </option>
                      ))
                    : children}
            </select>
            <Icon name="chevronDown" size={16} className="ds-control__chevron" />
        </div>
    );
});

function CheckLike({ kind, label, hint, disabled, className, ...rest }) {
    const box =
        kind === 'switch' ? (
            <span className="ds-switch__track" aria-hidden="true" />
        ) : (
            <span className="ds-check__box" aria-hidden="true">
                {kind === 'checkbox' && <Icon name="check" size={13} strokeWidth={3} />}
            </span>
        );
    return (
        <label className={cx('ds-check', `ds-check--${kind}`, disabled && 'is-disabled', className)}>
            <input
                className="ds-check__input"
                type={kind === 'radio' ? 'radio' : 'checkbox'}
                role={kind === 'switch' ? 'switch' : undefined}
                disabled={disabled}
                {...rest}
            />
            {box}
            {(label || hint) && (
                <span className="ds-check__text">
                    {label}
                    {hint && <span className="ds-check__hint">{hint}</span>}
                </span>
            )}
        </label>
    );
}

export const Checkbox = (props) => <CheckLike kind="checkbox" {...props} />;
export const Radio = (props) => <CheckLike kind="radio" {...props} />;
export const Switch = (props) => <CheckLike kind="switch" {...props} />;

// Переключатель видов: «Все / Активные / Архив». options: [{ value, label, count, icon }]
export function Segmented({ options, value, onChange, label, className }) {
    return (
        <div className={cx('ds-segmented', className)} role="tablist" aria-label={label}>
            {options.map((o) => {
                const active = o.value === value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        className={cx('ds-segmented__item', active && 'is-active')}
                        onClick={() => onChange?.(o.value)}
                    >
                        {o.icon && <Icon name={o.icon} size={15} />}
                        {o.label}
                        {o.count != null && <span className="ds-segmented__count">{o.count}</span>}
                    </button>
                );
            })}
        </div>
    );
}
